import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Award, ClipboardCheck, GraduationCap, ShieldCheck, Factory, Phone, Monitor, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { SEO, getLocalBusinessSchema } from "../components/SEO";
import { UpcomingTrainingSection } from "../components/UpcomingTrainingSection";

const SERVICES = [
  { icon: ClipboardCheck, title: "ISO 9001 Consulting & Implementation", text: "Gap assessments, documentation and certification readiness for manufacturers, service providers and public agencies.", to: "/services/iso-9001" },
  { icon: Factory, title: "Food Safety: ISO 22000, FSSC 22000, SQF & HACCP", text: "Support for food processors and agri-business exporters meeting customer, GFSI and FDA expectations.", to: "/services/iso-22000" },
  { icon: ShieldCheck, title: "Internal, Supplier & Third-Party Audits", text: "Independent audits led by a PECB Certified ISO 9001 Senior Lead Auditor who audits for US certification bodies.", to: "/services/auditing" },
  { icon: GraduationCap, title: "PECB Certified Training", text: "Live online and self-paced ISO 9001, ISO 27001, ISO 42001, ISO 45001 and more — exam and certification fee included.", to: "/courses" },
];

const DELIVERY = [
  { icon: Monitor, title: "Live online", text: "Instructor-led classes on US Eastern time — your team can join from anywhere in the country." },
  { icon: Building2, title: "On-site", text: "In-house training and consulting at your facility, available on request." },
];

export default function UsLanding() {
  return (
    <div className="min-h-screen" data-testid="us-landing">
      <SEO
        title="ISO Training & Consulting in the United States"
        description="ISO 9001, ISO 22000, ISO 45001 and PECB certified training and consulting for US organizations — live online nationwide or on-site. Based in Madison, Mississippi."
        url="/iso-training-consulting-usa"
        keywords={["ISO training USA", "ISO 9001 consultant United States", "ISO consultant Mississippi", "ISO 9001 Lead Auditor training USA", "food safety consultant USA", "ISO certification consulting US", "PECB training USA"]}
        structuredData={[{ ...getLocalBusinessSchema(), areaServed: { "@type": "Country", name: "United States" } }]}
      />

      <section className="bg-navy-darker text-white">
        <div className="container mx-auto px-4 lg:px-8 py-20 lg:py-28 max-w-5xl">
          <span className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-6">
            <MapPin className="h-3.5 w-3.5" /> Madison, Mississippi
          </span>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6" data-testid="us-landing-title">
            ISO Training & Consulting <span className="text-gold">in the United States</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mb-8">
            Based in Madison, MS, Compliance Management Systems offers ISO certification consulting and PECB certified training — delivered live online or on-site at your facility.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button size="lg" className="w-full sm:w-auto bg-gold hover:bg-gold-dark text-navy-darker font-semibold" data-testid="us-consult-btn">Book a Consultation <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <Link to="/courses"><Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10" data-testid="us-courses-btn">Browse PECB Courses</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <h2 className="font-heading font-bold text-3xl text-charcoal mb-4">Services we offer</h2>
          <p className="text-slate-600 mb-10 max-w-3xl">Our services are well suited to manufacturers, food and agri-business producers, energy and logistics companies, technology firms, healthcare providers and public agencies.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <Link key={s.title} to={s.to} className="group border rounded-xl p-6 transition-colors hover:border-brand hover:bg-brand/5">
                <s.icon className="h-7 w-7 text-brand mb-3" />
                <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-brand">{s.title}</h3>
                <p className="text-sm text-slate-600">{s.text}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <UpcomingTrainingSection />

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl grid lg:grid-cols-2 gap-12">
          <div>
            <Award className="h-8 w-8 text-brand mb-3" />
            <h2 className="font-heading font-bold text-2xl text-charcoal mb-3">Led by an ISO 9001 insider</h2>
            <p className="text-slate-600 mb-4">
              Marshalee Valentine Gordon is a PECB Certified ISO 9001 Senior Lead Auditor and trainer who contributed to ISO/TC 176/SC 2/WG 29 — the working group behind the ISO 9001 revision — and is a third-party lead auditor for certification bodies in the United States and the Caribbean.
            </p>
            <Link to="/consultants#marshalee-valentine" className="text-brand font-semibold hover:underline" data-testid="us-bio-link">Read her profile →</Link>
          </div>
          <div>
            <h2 className="font-heading font-bold text-2xl text-charcoal mb-4">How we deliver</h2>
            <div className="space-y-4 mb-5">
              {DELIVERY.map((d) => (
                <div key={d.title} className="flex gap-3">
                  <d.icon className="h-5 w-5 text-brand mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">{d.title}</p>
                    <p className="text-sm text-slate-600">{d.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-600 flex items-center gap-2"><Phone className="h-4 w-4 text-brand" /> US: <a href="tel:+19548301186" className="text-brand font-medium">+1 (954) 830-1186</a></p>
          </div>
        </div>
      </section>
    </div>
  );
}
